import React, { useRef } from 'react';
import gql from 'graphql-tag';
import { ApolloQueryResult, useApolloClient, useMutation } from '@apollo/client';
import EventsSynchronizer from '~Components/EventsSynchronizer';

import {
  AddTableEvents,
  AddTableEventsVariables,
  GetVenueTableEvents,
  GetVenueTableEventsVariables,
} from './__queries__';
import { useDispatch, useLogger } from '~Hooks';
import { useSocket } from '~Components/SocketManager';

const TABLE_EVENTS_SNAPSHOTS_QUERY = gql`
  query GetVenueTableEventsSnapshots($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      eventsSnapshot
    }
  }
`;

const TABLE_EVENTS_QUERY = gql`
  query GetVenueTableEvents($venueId: String!, $sentAfter: DateTime!, $lastKey: Json) {
    tableEvents(venueId: $venueId, sentAfter: $sentAfter, limit: 1000, lastKey: $lastKey) {
      lastKey
      items
    }
  }
`;

const ADD_TABLE_EVENT_MUTATION = gql`
  mutation AddTableEvents($venueId: String!, $events: Json!) {
    addTableEvents(venueId: $venueId, events: $events) {
      success
      message
    }
  }
`;

interface Props {
  venueId: string;
}

const TableSynchronizer: React.FunctionComponent<Props> = ({ venueId }) => {
  const client = useApolloClient();
  const dispatch = useDispatch();
  const socket = useSocket();
  const logger = useLogger('table-synchronizer');

  const eventQueryRunning = useRef(false);

  const [addTableEvents] = useMutation<AddTableEvents, AddTableEventsVariables>(
    ADD_TABLE_EVENT_MUTATION,
  );

  return (
    <EventsSynchronizer
      venueId={venueId}
      eventType="ADD_TABLE_EVENT"
      manyEventsType="ADD_TABLE_EVENTS"
      snapshotType="SET_TABLE_SNAPSHOT"
      pollingInterval={6000}
      eventSelector={(state) => state.tableEvents}
      snapshotSelector={(state) => state.tableSnapshot}
      /* Polling query for new events */
      queryEvents={async (timestamp) => {
        if (eventQueryRunning.current) {
          logger.warn('Fetch table events query already running, aborting fetch...');
          return {
            success: false,
            events: [],
            error: 'Query already running',
          };
        }

        eventQueryRunning.current = true;

        try {
          let success = false;
          let lastKey: string | null = null;
          const items = [];
          let response: ApolloQueryResult<GetVenueTableEvents> | null = null;

          let guard = 0;

          // Refetch events until all are fetched
          do {
            if (lastKey) {
              logger.info(`Fetching more table events... Fetch number ${guard + 1}}`, {
                lastKey,
              });
            }

            response = await client.query<GetVenueTableEvents, GetVenueTableEventsVariables>({
              query: TABLE_EVENTS_QUERY,
              variables: {
                venueId,
                lastKey,
                sentAfter: timestamp,
              },
              fetchPolicy: 'network-only',
            });

            success = Boolean(response.data?.tableEvents?.items);

            if (success) {
              items.push(...response.data.tableEvents.items);

              // If lastKey is set we need to paginate
              lastKey = response.data.tableEvents.lastKey;
            } else {
              // Error, try the whole thing again (even if we got some events)
              logger.error('Error fetching table events', {
                errorMessage: response.error?.message,
                response,
              });

              eventQueryRunning.current = false;

              return {
                success: false,
                events: [],
                error: response.error?.message || null,
              };
            }

            // Just for peace of mind - let's not get stuck in an infinite loop
            guard++;
          } while (success && lastKey && guard < 10);

          /**
           * Register that we actually got data.
           * Doesn't matter if we got new items/events or not, just that we have
           * established a connection with the backend and it sent us data
           *
           * If this action isnt triggered in a while, we will show a warning
           */
          dispatch({
            type: 'NEW_TABLE_DATA',
            eventCount: items.length,
          });

          eventQueryRunning.current = false;

          return {
            success,
            events: items,
            error: null,
          };
        } catch (e) {
          logger.error('Error fetching table events', {
            errorMessage: (e as any).message,
          });

          eventQueryRunning.current = false;

          return {
            success: false,
            events: [],
            error: (e as any).message,
          };
        }
      }}
      /* Snapshot query */
      querySnapshot={async () => {
        const response = await client.query({
          query: TABLE_EVENTS_SNAPSHOTS_QUERY,
          variables: {
            venueId,
          },
          fetchPolicy: 'network-only',
        });

        const snapshot = response.data?.venue?.eventsSnapshot;

        return {
          success: Boolean(response.data?.venue?.eventsSnapshot),
          snapshot: snapshot
            ? {
                items: snapshot.items,
                cheques: snapshot.cheques,
                logs: snapshot.logs,
                payments: snapshot.payments,
                notes: snapshot.notes,
                sessions: [],
              }
            : null,
          timestamp: snapshot?.date || null,
          error: response.error?.message || null,
        };
      }}
      addEventMutation={async (events) => {
        try {
          const response = await addTableEvents({
            variables: {
              venueId,
              events,
            },
          });

          if (!response.data?.addTableEvents?.success) {
            return {
              success: false,
              error: response.errors?.map((error) => error.message).join(', ') || 'Unknown',
            };
          }

          return {
            error: null,
            success: true,
          };
        } catch (e) {
          return {
            success: false,
            // @ts-ignore
            error: e.message,
          };
        }
      }}
      sendSocketMessage={(event) => {
        socket.send.tableEvent(event);
      }}
    />
  );
};

export default TableSynchronizer;
